/**
 * Interface languages.
 *
 * The app's own text is English; a dictionary per language maps the exact English wording to its
 * translation. `applyLanguage()` translates the page (text, aria-label, title, placeholder) and
 * keeps translating what the app draws later. Wording with no entry stays English. Arabic is laid
 * out right to left.
 */
import fr from './lang/fr.js';
import ar from './lang/ar.js';

export const LANGUAGES = Object.freeze([
  { code: 'en', label: 'English', dir: 'ltr' },
  { code: 'fr', label: 'Français', dir: 'ltr' },
  { code: 'ar', label: 'العربية', dir: 'rtl' },
]);

const DICTIONARIES = { fr, ar };
const KEY = 'pm.lang';
const ATTRS = ['aria-label', 'title', 'placeholder', 'alt'];

let current = 'en';
let observer = null;

const known = (code) => LANGUAGES.some((l) => l.code === code);

export function getLanguage() {
  return current;
}

/** The saved choice, else the browser's language when supported, else English. */
export function detectLanguage(nav = typeof navigator !== 'undefined' ? navigator : null, store = safeStorage()) {
  const saved = store?.getItem(KEY);
  if (saved && known(saved)) return saved;
  const guess = String(nav?.language || '').slice(0, 2).toLowerCase();
  return known(guess) ? guess : 'en';
}

export function saveLanguage(code, store = safeStorage()) {
  if (!known(code)) return;
  try {
    store?.setItem(KEY, code);
  } catch {
    /* private mode */
  }
}

function safeStorage() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

/** The translation of an English wording in the current language (or the wording itself). */
export function t(text) {
  const dict = DICTIONARIES[current];
  return (dict && dict[text]) || text;
}

function translateValue(value) {
  if (!value) return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  const out = t(trimmed);
  return out === trimmed ? value : value.replace(trimmed, out);
}

function translateNode(node) {
  if (node.nodeType === 3) {
    const next = translateValue(node.nodeValue);
    if (next !== node.nodeValue) node.nodeValue = next;
    return;
  }
  if (node.nodeType !== 1) return;
  if (/^(SCRIPT|STYLE|TEXTAREA)$/.test(node.tagName)) return;
  for (const attr of ATTRS) {
    const v = node.getAttribute?.(attr);
    if (v) {
      const next = translateValue(v);
      if (next !== v) node.setAttribute(attr, next);
    }
  }
  for (const child of node.childNodes) translateNode(child);
}

/** Switch the page to `code` (no reload needed for text drawn from now on; reload for the rest). */
export function applyLanguage(code, doc = typeof document !== 'undefined' ? document : null, { watch = true } = {}) {
  current = known(code) ? code : 'en';
  const lang = LANGUAGES.find((l) => l.code === current);
  if (!doc) return current;
  doc.documentElement.lang = lang.code;
  doc.documentElement.dir = lang.dir;
  observer?.disconnect();
  observer = null;
  if (current === 'en') return current;
  translateNode(doc.body);
  if (watch && typeof MutationObserver !== 'undefined') {
    observer = new MutationObserver((records) => {
      for (const r of records) {
        if (r.type === 'childList') for (const n of r.addedNodes) translateNode(n);
        else if (r.type === 'characterData') translateNode(r.target);
        else if (r.type === 'attributes') translateNode(r.target);
      }
    });
    observer.observe(doc.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
  }
  return current;
}
